/** SES-022 assignment-half harness: unstyled, disposable, exercises the four endpoints only. */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import type { ActivityRoleAssignment } from "@nebulize/sdk";

import { api, ApiError } from "../lib/api";

export const Route = createFileRoute("/harness/role-assignments")({
  component: RoleAssignmentHarness,
});

function refusalOf(error: unknown): string {
  if (error instanceof ApiError) return `${error.code}: ${error.message}`;
  return error instanceof Error ? error.message : "";
}

function RoleAssignmentHarness() {
  const [addedActivityId, setAddedActivityId] = useState("");
  const [role, setRole] = useState("");
  const [accountId, setAccountId] = useState("");
  const [catchAll, setCatchAll] = useState(false);
  const [assignmentId, setAssignmentId] = useState("");
  const [changeRole, setChangeRole] = useState("");
  const [changeAccountId, setChangeAccountId] = useState("");
  const [changeCatchAll, setChangeCatchAll] = useState(false);
  const [removeId, setRemoveId] = useState("");
  const [listAddedActivityId, setListAddedActivityId] = useState("");
  const [assignments, setAssignments] = useState<ActivityRoleAssignment[]>([]);
  const [lastError, setLastError] = useState("");
  const [lastResult, setLastResult] = useState("");

  async function assign() {
    setLastError("");
    try {
      const body = catchAll
        ? { role, catch_all: true }
        : { role, account_id: accountId };
      const created = await api.post<ActivityRoleAssignment>(
        `/v1/added-activities/${addedActivityId}/roles`,
        body,
      );
      setLastResult(JSON.stringify(created));
    } catch (error) {
      setLastError(refusalOf(error));
    }
  }

  async function change() {
    setLastError("");
    try {
      const body = changeCatchAll
        ? { role: changeRole, catch_all: true }
        : { role: changeRole, account_id: changeAccountId };
      const changed = await api.patch<ActivityRoleAssignment>(
        `/v1/activity-role-assignments/${assignmentId}`,
        body,
      );
      setLastResult(JSON.stringify(changed));
    } catch (error) {
      setLastError(refusalOf(error));
    }
  }

  async function remove() {
    setLastError("");
    try {
      await api.delete(`/v1/activity-role-assignments/${removeId}`);
      setLastResult(`removed ${removeId}`);
    } catch (error) {
      setLastError(refusalOf(error));
    }
  }

  async function list() {
    setLastError("");
    try {
      const found = await api.get<ActivityRoleAssignment[]>(
        `/v1/added-activities/${listAddedActivityId}/roles`,
      );
      setAssignments(found);
    } catch (error) {
      setLastError(refusalOf(error));
    }
  }

  return (
    <main>
      <h1>SES-022 role assignment harness</h1>
      <p>Unstyled on purpose. Exercises the four endpoints directly, nothing else.</p>

      <h2>Assign a role</h2>
      <p>
        <label>
          added_activity_id
          <input value={addedActivityId} onChange={(e) => setAddedActivityId(e.target.value)} />
        </label>
      </p>
      <p>
        <label>
          role
          <input value={role} onChange={(e) => setRole(e.target.value)} />
        </label>
      </p>
      <p>
        <label>
          <input type="checkbox" checked={catchAll} onChange={(e) => setCatchAll(e.target.checked)} />
          catch_all
        </label>
      </p>
      {catchAll ? null : (
        <p>
          <label>
            account_id
            <input value={accountId} onChange={(e) => setAccountId(e.target.value)} />
          </label>
        </p>
      )}
      <p>
        <button type="button" onClick={() => void assign()}>
          POST /v1/added-activities/&#123;added_activity_id&#125;/roles
        </button>
      </p>

      <h2>Change an assignment</h2>
      <p>
        <label>
          assignment_id
          <input value={assignmentId} onChange={(e) => setAssignmentId(e.target.value)} />
        </label>
      </p>
      <p>
        <label>
          role
          <input value={changeRole} onChange={(e) => setChangeRole(e.target.value)} />
        </label>
      </p>
      <p>
        <label>
          <input
            type="checkbox"
            checked={changeCatchAll}
            onChange={(e) => setChangeCatchAll(e.target.checked)}
          />
          catch_all
        </label>
      </p>
      {changeCatchAll ? null : (
        <p>
          <label>
            account_id
            <input value={changeAccountId} onChange={(e) => setChangeAccountId(e.target.value)} />
          </label>
        </p>
      )}
      <p>
        <button type="button" onClick={() => void change()}>
          PATCH /v1/activity-role-assignments/&#123;assignment_id&#125;
        </button>
      </p>

      <h2>Remove an assignment</h2>
      <p>
        <label>
          assignment_id
          <input value={removeId} onChange={(e) => setRemoveId(e.target.value)} />
        </label>
      </p>
      <p>
        <button type="button" onClick={() => void remove()}>
          DELETE /v1/activity-role-assignments/&#123;assignment_id&#125;
        </button>
      </p>

      <h2>List assignments on an added activity</h2>
      <p>
        <label>
          added_activity_id
          <input
            value={listAddedActivityId}
            onChange={(e) => setListAddedActivityId(e.target.value)}
          />
        </label>
      </p>
      <p>
        <button type="button" onClick={() => void list()}>
          GET /v1/added-activities/&#123;added_activity_id&#125;/roles
        </button>
      </p>
      <ul>
        {assignments.map((one) => (
          <li key={one.assignment_id}>
            {one.assignment_id} - role={one.role} account_id={one.account_id ?? "null"} catch_all=
            {String(one.catch_all)}
          </li>
        ))}
      </ul>

      <h2>Last result</h2>
      <pre>{lastResult}</pre>
      <p style={{ color: "red" }}>{lastError}</p>
    </main>
  );
}
